import { useEffect, useState, type FormEvent } from 'react';
import { Link, useLocation, useNavigate } from 'react-router-dom';

const navItems = [
  { label: '首页', to: '/' },
  { label: '电视剧', to: '/category/tv' },
  { label: '电影', to: '/category/movie' },
  { label: '综艺', to: '/category/variety' },
  { label: '关于我们', to: '/about' },
];

export default function Navbar() {
  const [scrolled, setScrolled] = useState(false);
  const [menuOpen, setMenuOpen] = useState(false);
  const [keyword, setKeyword] = useState('');
  const location = useLocation();
  const navigate = useNavigate();

  const isHome = location.pathname === '/';
  const transparent = isHome && !scrolled;

  useEffect(() => {
    const onScroll = () => setScrolled(window.scrollY > 20);
    onScroll();
    window.addEventListener('scroll', onScroll);
    return () => window.removeEventListener('scroll', onScroll);
  }, []);

  useEffect(() => {
    setMenuOpen(false);
  }, [location.pathname]);

  const handleSearch = (e: FormEvent) => {
    e.preventDefault();
    const q = keyword.trim();
    navigate(q ? `/search?q=${encodeURIComponent(q)}` : '/search');
    setMenuOpen(false);
  };

  return (
    <header
      className={`fixed top-0 left-0 right-0 z-50 transition-all duration-300 ${
        transparent
          ? 'bg-transparent'
          : 'bg-background-50/95 backdrop-blur-md border-b border-background-200/70'
      }`}
    >
      <div className="w-full px-4 md:px-8">
        <div className="h-16 md:h-20 flex items-center gap-4 md:gap-8">
          {/* 品牌 */}
          <Link to="/" className="flex items-center gap-2 shrink-0 cursor-pointer">
            <span className="w-9 h-9 md:w-10 md:h-10 rounded-lg bg-accent-500 flex items-center justify-center shrink-0">
              <i className="ri-play-fill text-xl md:text-2xl text-accent-950" />
            </span>
            <span
              className={`text-xl md:text-2xl font-black tracking-tight ${
                transparent ? 'text-background-50' : 'text-foreground-950'
              }`}
            >
              牛牛视频
            </span>
          </Link>

          {/* 桌面导航 */}
          <nav className="hidden lg:flex items-center gap-1 flex-1">
            {navItems.map((item) => {
              const active =
                item.to === '/'
                  ? location.pathname === '/'
                  : location.pathname.startsWith(item.to);
              return (
                <Link
                  key={item.to}
                  to={item.to}
                  className={`px-3.5 py-2 rounded-md text-sm font-medium whitespace-nowrap transition-colors cursor-pointer ${
                    transparent
                      ? active
                        ? 'text-accent-400'
                        : 'text-background-100 hover:text-background-50'
                      : active
                        ? 'text-primary-600 bg-primary-50'
                        : 'text-foreground-600 hover:text-primary-600 hover:bg-background-100'
                  }`}
                >
                  {item.label}
                </Link>
              );
            })}
          </nav>

          {/* 搜索（桌面） */}
          <form onSubmit={handleSearch} className="hidden md:flex items-center gap-2 shrink-0">
            <div
              className={`flex items-center gap-2 h-10 pl-3 pr-1 rounded-full border transition-colors ${
                transparent
                  ? 'bg-background-50/15 border-background-50/40'
                  : 'bg-background-100 border-background-200'
              }`}
            >
              <i
                className={`ri-search-line w-5 h-5 flex items-center justify-center text-sm ${
                  transparent ? 'text-background-100' : 'text-foreground-400'
                }`}
              />
              <input
                value={keyword}
                onChange={(e) => setKeyword(e.target.value)}
                placeholder="搜索影视名称"
                className={`w-32 lg:w-44 bg-transparent text-sm outline-none ${
                  transparent
                    ? 'text-background-50 placeholder:text-background-100/70'
                    : 'text-foreground-900 placeholder:text-foreground-400'
                }`}
              />
              <button
                type="submit"
                className="h-8 px-3 rounded-full bg-accent-500 hover:bg-accent-600 text-accent-950 text-xs font-semibold whitespace-nowrap transition-colors cursor-pointer"
              >
                搜索
              </button>
            </div>
          </form>

          {/* 移动端：搜索图标 + 汉堡 */}
          <div className="flex lg:hidden items-center gap-2 ml-auto">
            <Link
              to="/search"
              className={`w-9 h-9 flex items-center justify-center rounded-md cursor-pointer ${
                transparent ? 'text-background-50' : 'text-foreground-800'
              }`}
              aria-label="搜索"
            >
              <i className="ri-search-line text-lg" />
            </Link>
            <button
              type="button"
              onClick={() => setMenuOpen((v) => !v)}
              className={`w-9 h-9 flex items-center justify-center rounded-md cursor-pointer ${
                transparent ? 'text-background-50' : 'text-foreground-800'
              }`}
              aria-label="菜单"
            >
              <i className={menuOpen ? 'ri-close-line text-xl' : 'ri-menu-line text-xl'} />
            </button>
          </div>
        </div>
      </div>

      {/* 移动端菜单 */}
      {menuOpen && (
        <div className="lg:hidden bg-background-50 border-t border-background-200/70">
          <div className="px-4 py-3 space-y-1">
            {navItems.map((item) => (
              <Link
                key={item.to}
                to={item.to}
                className="block px-3 py-2.5 rounded-md text-sm font-medium text-foreground-700 hover:bg-background-100 hover:text-primary-600 transition-colors cursor-pointer"
              >
                {item.label}
              </Link>
            ))}
            <form onSubmit={handleSearch} className="flex items-center gap-2 pt-2">
              <input
                value={keyword}
                onChange={(e) => setKeyword(e.target.value)}
                placeholder="搜索影视名称"
                className="flex-1 h-10 px-3 rounded-md bg-background-100 border border-background-200 text-sm text-foreground-900 placeholder:text-foreground-400 outline-none"
              />
              <button
                type="submit"
                className="h-10 px-4 rounded-md bg-accent-500 hover:bg-accent-600 text-accent-950 text-sm font-semibold whitespace-nowrap transition-colors cursor-pointer"
              >
                搜索
              </button>
            </form>
          </div>
        </div>
      )}
    </header>
  );
}